import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletAPI, offerAPI, demandAPI, vendorProfileAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Wallet, Inbox, Send, Trophy } from 'lucide-react';
import { DashboardSkeleton } from '../../components/skeletons';

const S = {
  page: { padding: '24px 20px', background: 'var(--cream)', minHeight: '100vh' },
  header: { marginBottom: 24 },
  title: { fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 700, color: 'var(--char)' },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginTop: 4 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 },
  statCard: { 
    padding: '20px 24px', 
    borderRadius: 14, 
    background: '#fff', 
    border: '1.5px solid var(--border)', 
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    cursor: 'default'
  },
  statLabel: { fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 },
  statVal: { fontFamily: 'var(--font-serif)', fontSize: 30, fontWeight: 700 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: 'var(--char)', marginBottom: 12, fontFamily: 'var(--font-serif)' },
  demandCard: { 
    padding: 16, 
    borderRadius: 12, 
    background: '#fff', 
    border: '1.5px solid var(--border)', 
    marginBottom: 10, 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    flexWrap: 'wrap', 
    gap: 12,
    transition: 'all 0.2s ease'
  },
  tag: { display: 'inline-block', padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, marginRight: 6 },
  emptyBox: { textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 14, border: '1.5px solid var(--border)' },
};

export default function VendorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [offers, setOffers] = useState([]);
  const [demands, setDemands] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPromo, setShowPromo] = useState(false);
  const [promoAmount, setPromoAmount] = useState(0);

  useEffect(() => {
    if (wallet && wallet.systemMode === 'local_production' && user?._id) {
      const key = `showedFreeCreditPopup_${user._id}`;
      const bonusTx = (wallet.transactions || []).find(
        t => t.description === 'Local Production Free Signup Bonus'
      );
      if (bonusTx && !localStorage.getItem(key)) {
        setPromoAmount(bonusTx.amount);
        setShowPromo(true);
        localStorage.setItem(key, 'true');
      }
    }
  }, [wallet, user]);

  useEffect(() => {
    if (showPromo) {
      const timer = setTimeout(() => {
        setShowPromo(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showPromo]);

  useEffect(() => {
    Promise.all([
      walletAPI.me().then(r => setWallet(r.data.data)).catch(() => { }),
      offerAPI.mine().then(r => setOffers(r.data.data || [])).catch(() => { }),
      demandAPI.list({ status: 'active' }).then(r => setDemands(r.data.data || [])).catch(() => { }),
      vendorProfileAPI.me().then(r => setProfile(r.data.data)).catch(() => { }),
    ]).finally(() => setLoading(false));
  }, []);

  const kycStatus = profile?.kyc?.status || 'draft';
  const isLocalMode = wallet?.systemMode === 'local_production';
  const kycBanner = (() => {
    if (kycStatus === 'verified') return null;
    const map = {
      draft: { bg: '#FEF2F2', fg: '#991B1B', border: '#FCA5A5', text: '⚠️ KYC not submitted. You cannot receive leads until your KYC is verified.', cta: 'Complete KYC →' },
      submitted: { bg: '#FFF8E7', fg: '#8A5A00', border: '#EAC475', text: '⏳ KYC submitted — admin is reviewing. You will start receiving leads once verified.', cta: 'View status' },
      rejected: { bg: '#FEF2F2', fg: '#991B1B', border: '#FCA5A5', text: `❌ KYC rejected${profile?.kyc?.rejectionReason ? ': ' + profile.kyc.rejectionReason : '.'} Fix and resubmit.`, cta: 'Review & fix →' },
    };
    return map[kycStatus];
  })();

  const today = new Date().toDateString();
  const leadsToday = wallet?.transactions
    ? wallet.transactions.filter(t => t.type === 'debit' && t.demandId && new Date(t.createdAt).toDateString() === today).length
    : 0;
  const accepted = offers.filter(o => o.status === 'accepted').length;
  const winRate = offers.length ? Math.round((accepted / offers.length) * 100) : 0;

  const stats = [
    { 
      label: 'Wallet Balance', 
      value: wallet ? `\u20B9${wallet.balance?.toLocaleString('en-IN') || 0}` : '\u20B90', 
      color: 'var(--green)', 
      icon: <Wallet size={22} />,
      bgLt: 'var(--green-lt)'
    },
    { 
      label: 'Leads Today', 
      value: leadsToday, 
      color: 'var(--gold-dk)', 
      icon: <Inbox size={22} />,
      bgLt: 'var(--gold-lt)'
    },
    { 
      label: 'Offers Sent', 
      value: offers.length, 
      color: 'var(--green)', 
      icon: <Send size={22} />,
      bgLt: 'var(--green-lt)'
    },
    { 
      label: 'Win Rate', 
      value: `${winRate}%`, 
      color: winRate >= 50 ? 'var(--green)' : 'var(--gold-dk)', 
      icon: <Trophy size={22} />,
      bgLt: winRate >= 50 ? 'var(--green-lt)' : 'var(--gold-lt)'
    },
  ];

  const activeStats = user?.vendorType === 'financial'
    ? stats.filter(s => s.label === 'Wallet Balance' || s.label === 'Leads Today')
    : stats;

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div style={S.page}>
      {showPromo && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999
        }}>
          <style>{`
            @keyframes bounceIn {
              0% { transform: scale(0.3); opacity: 0; }
              50% { transform: scale(1.05); opacity: 0.8; }
              70% { transform: scale(0.9); opacity: 0.9; }
              100% { transform: scale(1); opacity: 1; }
            }
          `}</style>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: '40px 32px',
            width: '90%',
            maxWidth: 420,
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)',
            border: '2px solid #1B5C3A',
            animation: 'bounceIn 0.5s ease-out'
          }}>
            <div style={{ fontSize: 60, marginBottom: 12 }}>🎉</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: '#1B5C3A', margin: '0 0 12px 0', fontFamily: 'var(--font-serif)' }}>
              Welcome Offer! 🎁
            </h3>
            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.5, margin: '0 0 20px 0' }}>
              Congratulations! You received a free signup credit of <strong style={{ color: '#1B5C3A', fontSize: 18 }}>₹{promoAmount}</strong> automatically added to your wallet!
            </p>
            <div style={{
              background: '#E8F5EE',
              borderRadius: 12,
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 700,
              color: '#1B5C3A',
              display: 'inline-block'
            }}>
              Bid on live customer demands immediately! 🚀
            </div>
          </div>
        </div>
      )}
      <div style={S.header}>
        <div style={S.title}>Welcome back, {user?.fullName?.split(' ')[0] || 'Vendor'}</div>
        <div style={S.subtitle}>Here's your vendor dashboard overview</div>
      </div>

      {/* KYC banner — hidden once verified */}
      {kycBanner && (
        <div
          onClick={() => navigate('/vendor/kyc')}
          style={{
            background: kycBanner.bg, 
            color: kycBanner.fg, 
            borderRadius: 12,
            borderLeft: `5px solid ${kycBanner.fg}`,
            borderTop: `1px solid ${kycBanner.border}`,
            borderBottom: `1px solid ${kycBanner.border}`,
            borderRight: `1px solid ${kycBanner.border}`,
            padding: '16px 20px', 
            marginBottom: 16, 
            fontSize: 13, 
            fontWeight: 600,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            cursor: 'pointer', 
            gap: 12, 
            flexWrap: 'wrap',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <span>{kycBanner.text}</span>
          <span style={{ fontWeight: 800, textDecoration: 'underline' }}>{kycBanner.cta}</span>
        </div>
      )}

      {/* Platform Fee Banner — shown if not paid or expired */}
      {(() => {
        const now = new Date();
        const expiresAt = profile?.kyc?.platformFeeExpiresAt ? new Date(profile.kyc.platformFeeExpiresAt) : null;
        const isFeeExpired = !profile?.kyc?.platformFeePaid || !expiresAt || expiresAt < now;

        if (isLocalMode || !isFeeExpired) return null;

        return (
          <div
            onClick={() => navigate('/vendor/kyc')}
            style={{
              background: '#FFF8E7', 
              color: '#8A5A00', 
              borderRadius: 12,
              borderLeft: '5px solid #F5A623',
              borderTop: '1px solid #EAC475',
              borderBottom: '1px solid #EAC475',
              borderRight: '1px solid #EAC475',
              padding: '16px 20px', 
              marginBottom: 16, 
              fontSize: 13, 
              fontWeight: 600,
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer', 
              gap: 12,
              flexWrap: 'wrap',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 18 }}>💳</span>
              <span>Annual KYC/Platform Fee (₹500) is required to receive leads. {expiresAt ? `Expired on ${expiresAt.toLocaleDateString()}` : 'Not paid yet.'}</span>
            </div>
            <span style={{ fontWeight: 800, textDecoration: 'underline' }}>Pay Now →</span>
          </div>
        );
      })()}

      {/* Stat Cards */}
      <div style={S.grid4}>
        {activeStats.map((s, i) => (
          <div key={i} className="card" style={S.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              e.currentTarget.style.borderColor = s.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={S.statLabel}>{s.label}</div>
                <div style={{ ...S.statVal, color: s.color }}>{s.value}</div>
              </div>
              <div style={{ 
                fontSize: 22, 
                width: 46, 
                height: 46, 
                borderRadius: '50%', 
                backgroundColor: s.bgLt, 
                color: s.color,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {s.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Recharge */}
      {!isLocalMode && (
        <div className="card" 
          style={{ 
            padding: '20px 24px', 
            marginBottom: 28, 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'center', 
            justifyContent: 'space-between', 
            borderLeft: '5px solid var(--gold)',
            background: 'linear-gradient(135deg, var(--white) 0%, var(--gold-lt) 100%)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)' }}>Low on wallet balance?</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Recharge your wallet to keep receiving high-quality leads in real-time.</div>
          </div>
          <button className="btn btn-gold btn-md" onClick={() => navigate('/vendor/wallet')}
            style={{ padding: '10px 22px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(245,166,35,0.2)' }}>
            Recharge Wallet
          </button>
        </div>
      )}

      {/* Hot Demands */}
      <div style={S.sectionTitle}>Hot Demands</div>
      {demands.length === 0 ? (
        <div style={S.emptyBox}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No active demands right now</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Check back soon or set up lead filters to get notified</div>
        </div>
      ) : (
        demands.slice(0, 6).map(d => (
          <div key={d._id} className="card" style={S.demandCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--green)';
              e.currentTarget.style.boxShadow = 'var(--shadow)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
            }}
          >
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {d.category && (
                  <span style={{ ...S.tag, background: 'var(--green-lt)', color: 'var(--green)', marginRight: 0 }}>{d.category.name || d.category}</span>
                )}
                {d.location && (
                  <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>
                    📍 {typeof d.location === 'object'
                      ? [d.location.city, d.location.state].filter(Boolean).join(', ')
                      : d.location}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)', lineHeight: 1.3 }}>{d.title}</div>
              {d.budget && (
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4, fontWeight: 500 }}>Budget: <span style={{ color: 'var(--green)', fontWeight: 700 }}>{'\u20B9'}{d.budget.toLocaleString('en-IN')}</span></div>
              )}
            </div>
            <button className="btn btn-pri btn-sm" onClick={() => navigate('/vendor/demands')}
              style={{ padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              View Lead
            </button>
          </div>
        ))
      )}
    </div>
  );
}